import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { DataGridPremiumProps, GridApiPremium } from '@mui/x-data-grid-premium'
import { type ReactNode, type RefObject, useState } from 'react'
import Spacings from '../figma/tokens/Spacings'
import FigmaBox from '../mynt-components/components/FigmaBox'
import useTextVariant from '../mynt-components/hooks/useTextVariant'
import type { AntiloopTextType } from '../tiger/interfaces/Antiloop'
import { TextKeys } from '../tiger/libs/TextRepository'

const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500]
const DEFAULT_PAGE_SIZE = 50

type TableGridPropsArgs = {
  headerTextKey: AntiloopTextType
}

/**
 * Shared DataGrid configuration for the accounting and payment tables.
 *
 * Replaces the material-table `Options` object these tables used to share.
 * `sortingOrder` is spelled out because material-table ran with
 * `thirdSortClick: false`, while the grid's default third click clears the
 * sort. `autoHeight` keeps the grid growing with its content the way
 * material-table did, rather than needing a fixed height from the caller.
 */
export const useTableGridProps = ({ headerTextKey }: TableGridPropsArgs): Partial<DataGridPremiumProps> => {
  const headerStyles = useTextVariant(headerTextKey).style
  const rowStyles = useTextVariant(TextKeys.tableValue).style

  return {
    autoHeight: true,
    pagination: true,
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    sortingOrder: ['asc', 'desc'],
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    initialState: { pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } } },
    sx: {
      '& .MuiDataGrid-columnHeaderTitle': headerStyles,
      '& .MuiDataGrid-cell': rowStyles,
    },
  }
}

type TableHeaderProps = {
  title: ReactNode
  apiRef: RefObject<GridApiPremium | null>
  actions?: ReactNode
  initialSearch?: string
}

/**
 * Title row rendered above the grid, with the search box on the right.
 *
 * The grid's own toolbar sits inside the grid's border, so the search box lives out here
 * instead and drives the grid's quick filter through `apiRef`. Splitting on whitespace
 * matches the grid's default `quickFilterParser`.
 */
export const TableHeader = ({ title, apiRef, actions, initialSearch = '' }: TableHeaderProps) => {
  const [search, setSearch] = useState(initialSearch)

  const onSearchChange = (value: string) => {
    setSearch(value)
    apiRef.current?.setQuickFilterValues(value.split(' ').filter(Boolean))
  }

  return (
    <FigmaBox fullWidth direction="row" align="center" justify="space-between" bottom={Spacings.min}>
      <Typography variant="h6">{title}</Typography>
      <FigmaBox direction="row" align="center" gap={Spacings.small}>
        {actions}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{ width: 260 }}
        />
      </FigmaBox>
    </FigmaBox>
  )
}
